import { useCallback, useEffect, useState } from 'react';

import { calculateButtonStates, calculateScrollDistance } from './scrollGeometry';
import { setupScrollEndListener, setupScrollObservers } from './scrollListeners';

const useScrollObservers = (
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
	updateScrollState: () => void,
) => {
	useEffect(() => {
		const container = scrollContainerRef.current;
		if (container) {
			return setupScrollObservers(container, updateScrollState);
		}

		let cleanup: (() => void) | undefined;
		const checkInterval = setInterval(() => {
			const currentContainer = scrollContainerRef.current;
			if (currentContainer) {
				clearInterval(checkInterval);
				cleanup = setupScrollObservers(currentContainer, updateScrollState);
			}
		}, 50);

		return () => {
			clearInterval(checkInterval);
			cleanup?.();
		};
	}, [scrollContainerRef, updateScrollState]);
};

type UseMediaHitScrollControlData = {
	canScrollLeft: boolean;
	canScrollRight: boolean;
	hasOverflow: boolean;
};

type UseMediaHitScrollControlActions = {
	updateScrollState: () => void;
	scrollByOneMediaHit: (direction: 'left' | 'right') => void;
};

type UseMediaHitScrollControlResult = ModelHookResult<UseMediaHitScrollControlData, UseMediaHitScrollControlActions>;

export function useMediaHitScrollControl(
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
): UseMediaHitScrollControlResult {
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);
	const [hasOverflow, setHasOverflow] = useState(false);

	const updateScrollState = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const states = calculateButtonStates(container);
		setCanScrollLeft(states.canScrollLeft);
		setCanScrollRight(states.canScrollRight);
		setHasOverflow(states.hasOverflow);
	}, [scrollContainerRef]);

	useScrollObservers(scrollContainerRef, updateScrollState);

	const scrollByOneMediaHit = useCallback(
		(direction: 'left' | 'right') => {
			const container = scrollContainerRef.current;
			if (!container) return;

			const distance = calculateScrollDistance(container, direction);
			const newPosition = container.scrollLeft + distance;
			const maxScroll = container.scrollWidth - container.clientWidth;
			const clampedPosition = Math.max(0, Math.min(newPosition, maxScroll));

			container.scrollTo({ left: clampedPosition, behavior: 'smooth' });
			setupScrollEndListener(container, updateScrollState);
		},
		[scrollContainerRef, updateScrollState],
	);

	return {
		data: { canScrollLeft, canScrollRight, hasOverflow },
		actions: { updateScrollState, scrollByOneMediaHit },
	};
}
